import { useTranslation } from 'react-i18next';
import { Textarea, type TextareaProps } from './Textarea';
import { cx, describedBy } from './cx';

export interface CharacterCountProps extends TextareaProps {
  maxLength: number;
  value: string;
}

/**
 * idsk/components/character-count. The message is computed in React (the ID-SK script hard-codes Slovak text),
 * keeping the template markup: `idsk-character-count` wrapper, `{id}-info` message with aria-live="polite".
 * The native maxlength attribute is not set (GOV.UK pattern): users may exceed the limit and get an error.
 */
export function CharacterCount({ maxLength, value, id, className, extraDescribedBy, ...rest }: CharacterCountProps) {
  const { t } = useTranslation();
  const remaining = maxLength - (value?.length ?? 0);
  const over = remaining < 0;
  const message = over
    ? t('characterCount.over', { count: -remaining })
    : t('characterCount.remaining', { count: remaining });
  return (
    <div className="idsk-character-count" data-module="idsk-character-count" data-maxlength={maxLength}>
      <Textarea
        id={id}
        value={value}
        className={cx('govuk-js-character-count', over && 'govuk-textarea--error', className)}
        extraDescribedBy={describedBy(extraDescribedBy, `${id}-info`)}
        after={
          <span
            id={`${id}-info`}
            className={cx('govuk-character-count__message', over ? 'govuk-error-message' : 'govuk-hint')}
            aria-live="polite"
          >
            {message}
          </span>
        }
        {...rest}
      />
    </div>
  );
}
